import React, { useEffect, useState } from "react";
import { Button, DatePicker, DateRange, LabelValue, Select, Toast } from "../../../../Components";
import { FaPlaneArrival, FaPlaneDeparture } from "react-icons/fa";
import { TbArrowsExchange } from "react-icons/tb";
import AirportSearch from "../../Inputs/AirportSearch";
import { useNavigate } from "react-router-dom";
import { useUserConfig } from "../../../../Hooks/useUserConfig";
import SelectPassengers from "../../Inputs/SelectPassengers";
import moment from "moment";
import { CLASSES } from "Utils/constants";
import { saveRecentFlightSearch } from "../../../../Utils/recentSearches";

function FlightSearch({ from, to, date, range, passengers, cabinClass, mode, optimizee, onSearch = () => {} }) {
	const NAVIGATE = useNavigate();
	const { role, checkUserPermission } = useUserConfig();
	const isUser = role === "USER";
	const [highlightSwappedFields, setHighlightSwappedFields] = useState(false);

	const MODES = [
		{ label: "One Way", value: "oneway" },
		{ label: "Round Trip", value: "round" },
	];

	const [optimize, setOptimize] = useState(() => {
		if (optimizee === false){
			return false;
		}
		else{
			const saved = localStorage.getItem("optimize");
  			return saved === null ? true : JSON.parse(saved);
		}
	});

	useEffect(() => {
		localStorage.setItem("optimize", JSON.stringify(optimize));
	}, [optimize]);

	const [Values, setValues] = useState({
		from: from || {},
		to: to || {},
		date: date || moment().valueOf(),
		range: range,
		passengers: passengers || {
			adult: 1,
			child: 0,
			infant: 0,
		},
		cabinClass: cabinClass || CLASSES[0],
		mode: mode || MODES[0],
	});

	useEffect(() => {
		if (!from && !to && !date && !range && !passengers && !cabinClass && !mode) {
			return;
		}
		setValues((prev) => ({
			...prev,
			...(from ? { from } : {}),
			...(to ? { to } : {}),
			...(date ? { date } : {}),
			...(range ? { range } : {}),
			...(passengers ? { passengers } : {}),
			...(cabinClass ? { cabinClass } : {}),
			...(mode ? { mode } : {}),
		}));
	}, [from, to, date, range, passengers, cabinClass, mode]);

	const updateValues = (obj) => {
		setValues((prev) => ({ ...prev, ...obj }));
	};

	const switchMode = (option) => {
		if (option.value === "oneway") {
			updateValues({ mode: MODES[0] });
		} else {
			updateValues({ mode: MODES[1] });
		}
	};

	const swapAirports = () => {
		const temp = Values.from;
		updateValues({ from: Values.to, to: temp });
		setHighlightSwappedFields(true);
	};

	useEffect(() => {
		if (!highlightSwappedFields) {
			return undefined;
		}

		const timeout = setTimeout(() => {
			setHighlightSwappedFields(false);
		}, 2000);

		return () => clearTimeout(timeout);
	}, [highlightSwappedFields]);

	const fromAirportField = (
		<AirportSearch
			selected={Values.from}
			label="Traveling from"
			onSelect={(from) => updateValues({ from })}
			placeholder="Travelling from"
			icon={<FaPlaneDeparture />}
			inputClassName="transition-[background-color,box-shadow] duration-300"
			inputStyle={
				highlightSwappedFields
					? {
							backgroundColor: "rgba(245, 158, 11, 0.08)",
							boxShadow: "inset 0 0 0 1px rgba(245, 158, 11, 0.22)",
						}
					: undefined
			}
		/>
	);

	const searchFlights = () => {
		if (!Values.from?.airportCode) {
			return Toast.error("Please select From airport");
		}
		if (!Values.to?.airportCode) {
			return Toast.error("Please select To airport");
		}
		if (Values.mode?.value === "oneway" && !Values.date) {
			return Toast.error("Please select date");
		}
		if (Values.mode?.value === "round" && (!Values.range?.startDate || !Values.range?.endDate)) {
			return Toast.error("Please select travel dates");
		}
		onSearch();
		saveRecentFlightSearch({ values: Values, optimize });
		if (Values.mode.value === MODES[0].value) {
			NAVIGATE(
				`/app/travel/flights/oneway?fromCity=${Values.from?.cityName}&from=${Values.from?.airportName}&fromCode=${Values.from?.airportCode}&toCity=${Values.to?.cityName}&to=${Values.to?.airportName}&toCode=${Values.to?.airportCode}&date=${Values.date}&adult=${Values.passengers?.adult}&child=${Values.passengers?.child}&infant=${Values.passengers?.infant}&cabinClass=${Values.cabinClass?.value}&optimize=${optimize}`
			);
		} else {
			NAVIGATE(
				`/app/travel/flights/round?fromCity=${Values.from?.cityName}&from=${Values.from?.airportName}&fromCode=${Values.from?.airportCode}&toCity=${Values.to?.cityName}&to=${Values.to?.airportName}&toCode=${Values.to?.airportCode}&date=${Values.range.startDate}&returnDate=${Values.range.endDate}&adult=${Values.passengers?.adult}&child=${Values.passengers?.child}&infant=${Values.passengers?.infant}&cabinClass=${Values.cabinClass?.value}&optimize=${optimize}`
			);
		}
	};

	return (
		<div className="space-y-4">
			{/* Search Options: stack on mobile, row on sm+ — Optimize Results kept ticked in state but hidden from UI */}
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
				<LabelValue className="text-dark font-medium">Search Options</LabelValue>
				<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 w-full sm:w-auto">
					<div className="w-full sm:w-auto">
						<Select
							variant="outlined"
							btnClass="btn-sm w-full sm:w-auto !min-w-0 sm:!min-w-[7rem]"
							onChange={switchMode}
							selected={Values.mode}
							options={MODES}
						/>
					</div>
					<div className="w-full sm:w-auto">
						<Select
							variant="outlined"
							btnClass="btn-sm w-full sm:w-auto !min-w-0 sm:!min-w-[7rem]"
							onChange={(cabinClass) => updateValues({ cabinClass })}
							selected={Values.cabinClass}
							options={CLASSES}
						/>
					</div>
				</div>
			</div>
			<div className="p-3 md:p-4 rounded-lg bg-light border border-surface/30 shadow-sm">
				<div className="flex flex-col gap-4">
					{/* From / Swap / To: stacks on mobile, single row on md+ */}
					<div className="flex flex-col gap-2 md:flex-row md:items-end md:gap-4">
						<div className="flex items-end gap-1.5 md:hidden">
							<div className="flex-1 min-w-0">{fromAirportField}</div>
							{isUser && (
								<div className="shrink-0 pb-0.5">
									<button
										type="button"
										onClick={swapAirports}
										className="inline-flex h-8 w-8 items-center justify-center rounded-md active:bg-canvas active:scale-95 transition-colors"
										aria-label="Swap origin and destination"
									>
										<TbArrowsExchange className="h-5 w-5 rotate-90 text-secondary" />
									</button>
								</div>
							)}
						</div>
						<div className="hidden flex-1 min-w-0 md:block">{fromAirportField}</div>
						{isUser && (
							<div className="hidden md:flex md:justify-start md:pb-2">
								<button
									type="button"
									onClick={swapAirports}
									className="p-2 rounded-md hover:bg-canvas transition-colors"
									aria-label="Swap origin and destination"
								>
									<TbArrowsExchange className="w-5 h-5 text-secondary transition-transform duration-200 hover:rotate-180" />
								</button>
							</div>
						)}
						<div className="flex-1 min-w-0">
							<AirportSearch
								selected={Values.to}
								label="Going to"
								onSelect={(to) => updateValues({ to })}
								placeholder="Going to"
								icon={<FaPlaneArrival />}
								inputClassName="transition-[background-color,box-shadow] duration-300"
								inputStyle={
									highlightSwappedFields
										? {
												backgroundColor: "rgba(245, 158, 11, 0.08)",
												boxShadow: "inset 0 0 0 1px rgba(245, 158, 11, 0.22)",
											}
										: undefined
								}
							/>
						</div>
					</div>

					{/* Passengers / Date / Find: stacks on mobile, row on lg+ */}
					<div className="flex flex-col gap-4 lg:flex-row lg:items-end">
						{checkUserPermission("TA") && (
							<div className="flex-1 min-w-0">
								<SelectPassengers
									passengers={Values.passengers}
									onChange={(obj) =>
										updateValues({
											passengers: {
												...Values.passengers,
												...obj,
											},
										})
									}
									label="Passengers"
								/>
							</div>
						)}
						<div className="flex-1 min-w-0">
							{Values?.mode?.value === "oneway" ? (
								<DatePicker backDateSelection={false} label="Travel Date" setDate={(date) => updateValues({ date })} date={Values.date} />
							) : (
								<DateRange backDateSelection={false} label="Travel Dates" setRange={(range) => updateValues({ range })} range={Values.range} />
							)}
						</div>
						<div className="w-full lg:w-[10rem]">
							<Button onClick={searchFlights} variant="primary" className="w-full">
								Find
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default FlightSearch;
